import { Fragment } from 'react'
import {
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  MenuTrigger,
  Popover,
  Separator,
  SubmenuTrigger,
} from 'react-aria-components'
import type { ReactElement, ReactNode } from 'react'
import type { Placement } from 'react-aria-components'
import { cx } from '../../cx'

export interface MenuOption {
  id: string
  label: ReactNode
  /** Right-aligned hint: a shortcut, a count, a status. */
  hint?: ReactNode
  isDisabled?: boolean
  /** Renders a separator above this item. */
  separatorBefore?: boolean
  onAction?: () => void
  /** Nested submenu. */
  items?: MenuOption[]
}

export interface MenuProps {
  /** Must be a single focusable element. */
  trigger: ReactElement
  items: MenuOption[]
  placement?: Placement
  className?: string
  onAction?: (id: string) => void
}

/**
 * Build the children as a FLAT array.
 *
 * React Aria's Menu is a collection component: it walks its direct children to
 * build the item collection. Wrapping items in a <div> (or any non-collection
 * element) yields an empty menu that renders as an empty popover. Arrays and
 * keyed Fragments containing only collection elements are fine.
 */
function renderItems(items: MenuOption[], onAction?: (id: string) => void): ReactNode[] {
  return items.map((item) => {
    const separator = item.separatorBefore ? (
      <Separator key={`${item.id}-sep`} className="bui-menu-div" />
    ) : null

    if (item.items?.length) {
      return (
        <Fragment key={item.id}>
          {separator}
          <SubmenuTrigger>
            <AriaMenuItem className="bui-menu-item" isDisabled={item.isDisabled}>
              <span>{item.label}</span>
              <span className="kbd" aria-hidden="true">›</span>
            </AriaMenuItem>
            <Popover offset={2}>
              <AriaMenu className="bui-menu-list">
                {renderItems(item.items, onAction)}
              </AriaMenu>
            </Popover>
          </SubmenuTrigger>
        </Fragment>
      )
    }

    return (
      <Fragment key={item.id}>
        {separator}
        <AriaMenuItem
          id={item.id}
          className="bui-menu-item"
          isDisabled={item.isDisabled}
          onAction={() => {
            item.onAction?.()
            onAction?.(item.id)
          }}
        >
          <span>{item.label}</span>
          {item.hint ? <span className="kbd">{item.hint}</span> : null}
        </AriaMenuItem>
      </Fragment>
    )
  })
}

/**
 * Full menu semantics from React Aria: roving tabindex, arrow-key navigation,
 * type-ahead, Escape to close, focus returned to the trigger, and correct
 * role="menu"/"menuitem" wiring. Submenus open on ArrowRight, close on ArrowLeft.
 */
export function Menu({
  trigger,
  items,
  placement = 'bottom start',
  className,
  onAction,
}: MenuProps) {
  return (
    <MenuTrigger>
      {trigger}
      <Popover placement={placement} offset={6} className={cx(className)}>
        <AriaMenu className="bui-menu-list">{renderItems(items, onAction)}</AriaMenu>
      </Popover>
    </MenuTrigger>
  )
}
